<script setup lang="ts">
import { watch, ref, onBeforeMount } from "vue";
import { storeToRefs } from "pinia";
import { useUserStore } from "../stores/user";
import type { Locales } from "../../types/tabs.ts";
import type { SearchSpaConfig } from "../../types/search-spa-config.ts";
import { useI18n } from "vue-i18n";
import { getSearchSpaConfig } from "../utils/searchSpaConfig";
import SearchContainer from "./SearchContainer/SearchContainer.vue";

const { locale } = useI18n();
const language = locale.value as Locales;

const searchSpaConfig = ref<SearchSpaConfig>();

const userStore = useUserStore();
const { groups: audience } = storeToRefs(userStore);

async function updateAudience() {
  // when the audience changes a new searchSpaConfig is generated

  let sirevsid = null;

  const isB2b = await userStore.isB2BUser();
  if (isB2b) {
    sirevsid = await userStore.getSirevSid();
  }

  searchSpaConfig.value = getSearchSpaConfig(
    audience.value,
    language,
    sirevsid ?? isB2b
  );
}

watch(audience, updateAudience);

if (audience.value) {
  // if the audience is already loaded then immediately update
  updateAudience();
}

onBeforeMount(() => {
  userStore.getUserInfo();
});

const endpoints =
  "(payment-traveller-info|info-voyageurs-paiement|confirmation)";

const hidePathEndRegex = new RegExp(`\\/${endpoints}$`);

const show = ref(isSpaShown(document.location.pathname));

function isSpaShown(path: string): boolean {
  return !hidePathEndRegex.test(path);
}

// hide the entire search-spa on the checkout or confirmation pages
function onURLChange(path: string) {
  show.value = isSpaShown(path);
}

// customURLChange events are dispatched by packages-spa src/router/index.ts when the router changes the URL
window.addEventListener("customURLChange", function (event: Event) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  onURLChange(event?.detail.path);
});

// popstate detects browser URL changes (back/forward button, initial page loads)
window.addEventListener("popstate", function () {
  onURLChange(document.location.pathname);
});
</script>
<template>
  <search-container
    v-if="searchSpaConfig && show"
    :searchSpaConfig="searchSpaConfig"
  />
</template>
